import type { Timestamp } from '@bufbuild/protobuf/wkt'
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { LicenseType } from '@ebalance/proto/bfftypes/v1/license_type_pb'
import type {
  BgMasterListItem,
  SubmitterMasterListItem,
} from '@ebalance/proto/bffuser/v1/occto_master_model_pb'
import {
  WorkflowCaseStatus,
  WorkflowCaseViewerAction,
} from '@ebalance/proto/bffuser/v1/workflow_case_model_pb'
import {
  type WorkflowCaseTag,
  type WorkflowSection,
} from '@ebalance/proto/bffuser/v1/workflow_template_model_pb'
import type { Date as ProtoDate } from '@ebalance/proto/google/type/date_pb'

import { NO_VALUE_LABEL } from '~/models/workflowCase'
import { workflowSectionLabelKey } from '~/models/workflowSection'
import {
  type WorkflowTargetCodeKind,
  workflowTargetCodeKindOfBgLicense,
} from '~/pages/WorkflowCases/models/workflowTargetCodeKind'

/**
 * ケース一覧行型。
 *
 * spec #1515/B1/B2 で name / note / completionNote / areaCodes (複数) を削除し、
 * workflowId / bgCode / bgName / repBusinessName / areaCode (単一) を追加した。
 * workflow-tags slice で schedule (区分) field を廃止し tags に置き換えた
 * (`workflowCaseScheduleLabelKey` は削除済み。詳細は AC-TAG-001〜004 参照)。
 * workflow-delete-transition design で中断/再開を撤去し、deletedAt/deletedBy
 * (不可逆な削除メタ) を追加した。workflow-delete-dialog-prototype-simplification
 * design で削除理由 field と hasSubmittedArtifact (取りやめ警告の判定入力) を
 * 撤去した (削除確認ダイアログから削除理由選択・広域機関警告 UI ごと撤去)。
 */
export type WorkflowCaseRow = {
  id: string
  /** human-readable business key (例: R-D1-20260423-2E3B) */
  workflowId: string
  workflowTemplateId: string
  workflowTemplateName: string
  targetDate: ProtoDate | undefined
  tags: WorkflowCaseTag[]
  section: WorkflowSection
  /** 単一エリア (spec #1515 で area_codes[] → area_code 単一化) */
  areaCode: AreaCode
  /**
   * BG コード。未設定 = 「BG なし」 (= 雛形のサブステップが BG を要する画面
   * 〈AC-WFW-003j〉に 1 つもリンクせず BG 軸を持たないケース)。
   * 表示は `WORKFLOW_CASE_BG_ABSENT_LABEL` で「—」にフォールバックする。
   */
  bgCode: string | undefined
  bgName: string | undefined
  repBusinessName: string | undefined
  status: WorkflowCaseStatus
  progress: { done: number; total: number }
  /**
   * 現在アクティブな大ステップ名 (進捗セルの上段)。 全完了・ステップ無しは undefined。
   * 導出は `models/activeBigStep.ts` (spec AC-WFW-001 の「先頭の未完了サブステップ」 基準)。
   */
  activeBigStepName: string | undefined
  // optimistic lock 用に proto Timestamp で保持する。表示時のみ timestampDate で Date 変換。
  completedAt: Timestamp | undefined
  completedBy: string | undefined
  createdAt: Timestamp | undefined
  // optimistic lock 用に proto Timestamp で保持する。表示時のみ timestampDate で Date 変換。
  updatedAt: Timestamp | undefined
  /** 削除日時 (workflow-delete-transition design、AC-08)。不可逆・表示時のみ Date 変換。 */
  deletedAt: Timestamp | undefined
  deletedBy: string | undefined
  /**
   * 対応者 = 現在ボールを持っている人 (= 進行中サブステップの担当者、AC-WFW-001)。
   * backend が導出済みの eauth member ID 列で、重複排除済み・大ステップ →
   * サブステップの display_order 昇順。空配列 = 対応者なし (完了 / 削除ケース、
   * および担当者未設定のケース) で `WORKFLOW_CASE_NO_VALUE_LABEL` を出す。
   */
  responderMemberIds: string[]
  /**
   * あなたの対応 (要承認 / 要対応 / 待ち / —)。backend が viewer 単位で導出
   * する (workflow-case-list-12col-viewer-action-4value slice: 4値化)。
   * 要承認・要対応ボタンは常に詳細画面へ遷移する (2026-08-19 spec AC-WFW-001
   * §243 追従改訂 D5) ため、遷移先を運ぶ field はこの型に無い (旧
   * `viewerActionRequiredLinkedPageKind` / `viewerActionRequiredLinkedPlanId`
   * は D6 で撤去)。
   */
  viewerAction: WorkflowCaseViewerAction
}

/**
 * WorkflowSection → i18n key (workflowCases 画面向け)。
 * `~/models/workflowSection` の canonical 定義を workflowCases 用に薄くラップする。
 */
export function workflowCaseSectionLabelKey(
  section: WorkflowSection,
): string | undefined {
  return workflowSectionLabelKey('workflowCases', section)
}

/**
 * 一覧 BG コード列の License バッジに使う、bgCode → 種別の突合 Map を作る (spec AC-WFW-001)。
 *
 * WorkflowCase は種別を持たないため、BG マスタ (需要BG・自己託送BG) と計画提出者
 * マスタ (License = 発電) を bgCode で突合する。BG マスタの発電 BG・需要抑制 BG と、
 * どちらのマスタにも無いコードは Map に入れない (バッジを出さない)。
 */
export function buildWorkflowTargetCodeKindByCode(
  bgMasterItems: ReadonlyArray<
    Pick<BgMasterListItem, 'bgCode' | 'licenseType'>
  >,
  submitterMasterItems: ReadonlyArray<
    Pick<SubmitterMasterListItem, 'submitterCode' | 'licenseType'>
  >,
): Map<string, WorkflowTargetCodeKind> {
  const kindByCode = new Map<string, WorkflowTargetCodeKind>()
  for (const bg of bgMasterItems) {
    const kind = workflowTargetCodeKindOfBgLicense(bg.licenseType)
    if (kind !== undefined) kindByCode.set(bg.bgCode, kind)
  }
  for (const submitter of submitterMasterItems) {
    if (submitter.licenseType === LicenseType.GENERATION) {
      kindByCode.set(submitter.submitterCode, 'generation')
    }
  }
  return kindByCode
}

const BG_LICENSE_BADGE_KEY: Record<WorkflowTargetCodeKind, string> = {
  demandBg: 'page:workflowCases.column.bgLicenseBadge.demand',
  selfWheelingBg: 'page:workflowCases.column.bgLicenseBadge.selfWheeling',
  generation: 'page:workflowCases.column.bgLicenseBadge.generation',
}

/** 種別 → 一覧 BG コード列の License バッジ (需要 / 自託 / 発電) の i18n key。 */
export function workflowCaseBgLicenseBadgeKey(
  kind: WorkflowTargetCodeKind,
): string {
  return BG_LICENSE_BADGE_KEY[kind]
}

/**
 * あなたの対応セルのラベル key (AC-WFW-001 / AC-05、4値化: workflow-case-
 * list-12col-viewer-action-4value slice)。UNSPECIFIED は List 以外の応答が
 * 返す「未供給」の印だが、一覧は必ず List 経由なので実際には到達しない。到達した
 * 場合も「対象なし」と同じ扱いにする (= 生の enum 名を出さない)。REQUIRED /
 * APPROVAL_REQUIRED はボタン表示 (`CaseTable.tsx`) が別途分岐するため、この
 * 関数はテキスト表示の WAITING のみを解決する。
 */
export function workflowCaseViewerActionLabelKey(
  action: WorkflowCaseViewerAction,
): string | undefined {
  switch (action) {
    case WorkflowCaseViewerAction.WAITING:
      return 'page:workflowCases.viewerAction.waiting'
    default:
      return undefined
  }
}

/**
 * BG 軸を持たないケース (= bgCode 未設定) の BG 表示。空欄のままだと「BG の取得に
 * 失敗した」のか「BG 軸を持たない雛形なのか」が区別できないため、他列の「対象なし」
 * 表記 (`あなたの対応` の「—」) と揃えた em dash を出す (AC-11)。
 */
export const WORKFLOW_CASE_BG_ABSENT_LABEL = '—'

/**
 * 「対象なし」を表す em dash。終端の対応者 / あなたの対応 = NONE の各セルで
 * 共通に使う (AC-WFW-001、workflow-list-action-columns design)。
 *
 * `WORKFLOW_CASE_UNRESOLVED_MEMBER_LABEL` の hyphen とは意図的に別字: em dash は
 * 「そもそも対象が無い」、hyphen は「対象はあるが表示名を解決できなかった」を表す
 * (= 既存の監査列と同じ使い分け)。i18n しないのは記号であって語ではないため。
 *
 * 表記規約の SoT は shared model の `~/models/workflowCase` の `NO_VALUE_LABEL`
 * (エリア列の `areaLabelOrDash` と同じ「対象なし」記号)。同一テーブル内で
 * 記号がずれないよう re-export し、二重定義の手動同期を避ける。
 */
export const WORKFLOW_CASE_NO_VALUE_LABEL = NO_VALUE_LABEL

/**
 * member 一覧で解決できなかった member ID の表示 (AC-09)。生の eauth member ID
 * (= auth0 subject) は画面に出さない。
 */
export const WORKFLOW_CASE_UNRESOLVED_MEMBER_LABEL = '-'
